// SearchLocationTag.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Axios from "axios";
import { useLocation, useHistory } from "react-router-dom";
import Icon from "@identitybuilding/idb-react-iconlib";
import { toggleMap } from "../../actions";

const SearchLocationTag = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const lang = useSelector((state) => state.general.lang);

  // Store data from backend
  const [backendMunicipalityData, setBackendMunicipalityData] = useState([]);
  const [backendSubMunicipalityData, setBackendSubMunicipalityData] = useState([]);
  const [backendProvinceData, setBackendProvinceData] = useState([]);
  const [backendRegionData, setBackendRegionData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Will hold slugs extracted from the URL if Redux is empty
  const [slugsFromPath, setSlugsFromPath] = useState([]);

  // 1) Extract location slugs from URL path, e.g. /zoeken/aaigem,erpe-mere/
  const getLocationsFromPath = () => {
    const match = location.pathname.match(/\/${window.location.href.includes("reseauentrepreneurs") ? "chercher" : "zoeken"}\/([^/]+)/); // Match the "zoeken/{locations}" segment
    if (match && match[1]) {
      const decodedSegment = decodeURIComponent(match[1]);
      return decodedSegment.split(",");
    }
    return [];
  };

  // 2) On mount or when pathname changes, capture any slugs in the URL
  useEffect(() => {
    setSlugsFromPath(getLocationsFromPath());
  }, [location.pathname]);

  // 3) If Redux has locations, prefer them; otherwise, use the URL slugs
  //    so you can handle both “facilities”.
  const reduxLocations = props.search && props.search.location
    ? props.search.location.map((loc) => loc.slug)
    : [];
  const finalSlugs =
    reduxLocations && reduxLocations.length > 0 ? reduxLocations : slugsFromPath;

  // Fetch data from backend
  const fetchMunicipalityData = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/municipality/?paginate=false&fields=id,name_nl,name_fr,name_de,name_en,pkancode,slug`,
        {
          headers: {
            Authorization: "Token ae0e4f2df7b0d54ce458ca65ca53849e92691e86",
          },
        }
      );
      setBackendMunicipalityData(response.data);
    } catch (error) {
      console.error("Error fetching municipality data:", error);
    }
  };
  const fetchSubMunicipalityData = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/submunicipality/?paginate=false&fields=id,name_nl,name_fr,name_de,name_en,pkancode,slug`,
        {
          headers: {
            Authorization: "Token ae0e4f2df7b0d54ce458ca65ca53849e92691e86",
          },
        }
      );
      setBackendSubMunicipalityData(response.data);
    } catch (error) {
      console.error("Error fetching submunicipality data:", error);
    }
  };
  const fetchProvinceData = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/province/?paginate=false&fields=id,name,nis_code,slug`,
        {
          headers: {
            Authorization: "Token ae0e4f2df7b0d54ce458ca65ca53849e92691e86",
          },
        }
      );
      setBackendProvinceData(response.data);
    } catch (error) {
      console.error("Error fetching province data:", error);
    }
  };
  const fetchRegionData = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_MEDIA_URL}/core/new-api/geo/region/?paginate=false&fields=id,name,nis_code,slug`,
        {
          headers: {
            Authorization: "Token ae0e4f2df7b0d54ce458ca65ca53849e92691e86",
          },
        }
      );
      setBackendRegionData(response.data);
    } catch (error) {
      console.error("Error fetching region data:", error);
    }
  };

  // 4) Fetch all location data once on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchMunicipalityData(),
        fetchSubMunicipalityData(),
        fetchProvinceData(),
        fetchRegionData(),
      ]);
      setLoading(false);
    };
    fetchData();
  }, []);

  // 5) Helper to get name + pkancode by slug
  const getNameAndPkanCode = (slug) => {
    const combinedData = [
      ...backendMunicipalityData,
      ...backendSubMunicipalityData,
      ...backendProvinceData,
      ...backendRegionData,
    ];

  //   const normalizeSlug = (slug) => slug.replace(/-\d+$/, '');

  //   const normalizeSlug = (s) => (s ? s.replace(/-\d+$/, '') : ''); // Ensure slug is a string before calling replace

  //   const normalizedSlug = normalizeSlug(slug);
  //   const entry = combinedData.find((item) => item.slug === slug);
  //   const entry = combinedData.find((item) => normalizeSlug(item.slug) === normalizedSlug);
  
  //   const entry = combinedData.find((item) => item.slug === slug);
  //   const entry = combinedData.find((item) => normalizeSlug(item.slug) === normalizedSlug);

  const normalizeSlug = (s) => (s ? s.replace(/-\d+$/, '') : '');
  const normalizedSlug = normalizeSlug(slug);

  if (combinedData.length === 0) {
    console.warn("No data available in combinedData yet.");
    return { name: slug, pkancode: null };
  }

  // First, try an **exact match**
  let entry = combinedData.find((item) => item.slug === slug);

  // If no exact match, try the **normalized match**
  if (!entry) {
    entry = combinedData.find((item) => normalizeSlug(item.slug) === normalizedSlug);
  }
    if (entry) {
      const language = lang; // or "name_nl" / "name_fr" / ...
      return {
        name:
          entry[language] ||
          entry.name_nl ||
          entry.name_fr ||
          entry.name_de ||
          entry.name_en ||
          entry.name ||
          slug,
        pkancode: entry.pkancode || entry.nis_code || null,
      };
    }
    // Fallback
    return { name: slug, pkancode: null };
  };

  return (
    <div
      id="location_tags_container"
      style={{
        alignItems: "center",
        overflowX: "auto",
        whiteSpace: "nowrap",
        maxWidth: "260px",
        padding: "2px 4px",
        borderRadius: "16px",
        backgroundColor: "#fff",
      }}
    >
      {loading ? (
        <div className="loadingScreen">
          <Icon name="Loading" animated />
        </div>
      ) : (
        finalSlugs.map((slug, index) => {
          const { name, pkancode } = getNameAndPkanCode(slug);
          if (!pkancode) {
            // No match found in your backend data => you can still display a fallback
            return null;
          }
          return (
            <React.Fragment key={index}>
              <div
                title={name}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  borderRadius: "25px",
                  padding: "2px 8px",
                  border: pkancode ? "1px solid orange" : "none",
                  marginRight: "6px",
                  fontSize: "11px",
                  color: "#707070",
                  fontWeight: "400",
                  whiteSpace: "nowrap",
                  height: "26px",
                  marginTop: "4px",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: "25px",
                    backgroundColor: "#fff",
                    padding: "2px",
                    cursor: "pointer",
                    width: "20px",
                    height: "20px",
                    marginLeft: "-5px",
                    marginRight: "5px",
                    color: "gray",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(toggleMap());
                  }}
                >
                  <Icon
                    name="CloseCircle"
                    style={{
                      fontSize: "12px",
                      color: "orange",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // If there is only one location, maybe redirect away
                      const newSlugs = finalSlugs.filter((x) => x !== slug);
                      if (newSlugs.length === 0) {
                        window.location.href = "https://ondernemersnetwerk.be";
                        return;
                      }
                      // Build the new path: /zoeken/{slug,slug}/Pagina=1
                      const updatedPath =
                        newSlugs.length > 0
                          ? `/${window.location.href.includes("reseauentrepreneurs") ? "chercher" : "zoeken"}/${encodeURIComponent(
                              newSlugs.join(",")
                            )}/${window.location.href.includes("reseauentrepreneurs") ? "page" : "pagina"}=1`
                          : `/${window.location.href.includes("reseauentrepreneurs") ? "chercher" : "zoeken"}/${window.location.href.includes("reseauentrepreneurs") ? "page" : "pagina"}=1`;
                      history.push(updatedPath);
                      window.location.reload();
                    }}
                  />
                </div>
                <span>{`${pkancode} | ${name}`}</span>
              </div>
            </React.Fragment>
          );
        })
      )}
    </div>
  );
};

export default SearchLocationTag;
